import type { JSX } from "react";
import { getUnitCategoryName } from "verdi-common/i18n";
import { useSupportedLocale } from "@/hooks/use-locale";
import { cn } from "@/utils/class-name";
import { AxisSide, type AxisSpec } from "../../ChartSpec";
import { useGetDisplayInfo } from "../../units/use-get-display-info";
import { INK, RULE_COLOR } from "../panel-tokens";
import type { LeftYAxisSelection } from "../use-left-y-axis-selection";
import { hasUnitCell, UnitCell } from "./unit-cell";

// Whole-pixel sizes and line boxes: a 12.5px font or a centred odd-height line box lands on half pixels at 1x.
const NAME_CLASS = "min-w-0 py-[5px] text-[12px] leading-[14px]";

/** A unit cell, its rule and the axis name, spaced by one gap. */
const SIDE_CLASS = "flex min-w-0 items-center gap-[8px]";

/** The short vertical rule between a unit cell and the names, the height of a text line. */
function Rule(): JSX.Element {
    return <span aria-hidden className={"h-[14px] w-px shrink-0"} style={{ backgroundColor: RULE_COLOR }} />;
}

/** One axis: its unit cell, a rule, and its category name. The right side mirrors the left. */
function HeaderSide({
    axis,
    leftAxisSelection,
    align = "start",
}: {
    axis: AxisSpec;
    leftAxisSelection?: LeftYAxisSelection;
    align?: "start" | "end";
}): JSX.Element {
    const getDisplayInfo = useGetDisplayInfo();
    const locale = useSupportedLocale();

    // The category, not axis.label: the unit cell already shows the label, so the name would repeat it.
    const name = (
        <div className={NAME_CLASS} style={{ color: INK }}>
            {getUnitCategoryName(axis.unitCategory, locale)}
        </div>
    );
    const showCell = hasUnitCell(axis, getDisplayInfo, leftAxisSelection);
    const cell = showCell && <UnitCell axis={axis} leftAxisSelection={leftAxisSelection} />;
    const rule = showCell && <Rule />;

    if (align === "end") {
        return (
            <div className={cn(SIDE_CLASS, "ml-auto text-right")}>
                {name}
                {rule}
                {cell}
            </div>
        );
    }
    return (
        <div className={SIDE_CLASS}>
            {cell}
            {rule}
            {name}
        </div>
    );
}

const STRIP_CLASS = "flex w-full items-stretch gap-[10px] px-[10px] pt-[4px]";

/** The panel's header strip: the active left axis at the start, the right axis at the end, or the focused axis alone on its side. */
export function DataViewerPanelHeader({
    axes,
    leftAxisSelection,
    focusedAxis,
}: {
    axes: AxisSpec[];
    leftAxisSelection: LeftYAxisSelection;
    focusedAxis?: AxisSpec;
}): JSX.Element {
    if (focusedAxis) {
        // No selection, so no metric picker; the unit picker doesn't need one.
        return (
            <div className={STRIP_CLASS}>
                <HeaderSide axis={focusedAxis} align={focusedAxis.side === AxisSide.Right ? "end" : "start"} />
            </div>
        );
    }

    const leftAxis = leftAxisSelection.activeLeftAxis;
    const rightAxis = axes.find((axis) => axis.side === AxisSide.Right);

    return (
        <div className={STRIP_CLASS}>
            {leftAxis && <HeaderSide axis={leftAxis} leftAxisSelection={leftAxisSelection} />}
            {rightAxis && <HeaderSide axis={rightAxis} align={"end"} />}
        </div>
    );
}
